import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import CreateDocumentForm from "components/document/Create/CreateDocumentForm/CreateDocumentForm";
import { useNavigate, useParams } from "react-router-dom";
import { DOCUMENT_CATEGORY_UUID, DOCUMENT_UUID } from "config/constant";
import {
  DocumentAttachmentFileRequest,
  NoneOrderTagDetailResponse,
  TempDocumentDetailResponse,
} from "apis/data-contracts";
import Toast, { useToast } from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import Button from "components/foundation/Button";
import { useFormContext } from "react-hook-form";
import ContractInfoStep2 from "components/document/Create/CreateDocumentForm/docClass/analysis/ContractInfoStep2";
import { useTempDocumentDetailQuery } from "features/document/hooks/queries/useTempDocumentDetailQuery";
import Modal from "components/foundation/Modal/Modal";
import ContractInfoStep1Modal from "components/document/Create/CreateDocumentForm/docClass/analysis/ContractInfoStep1Modal";
import {
  ATTACHMENT_FILES,
  DECISION_MANAGER_FIELD_NAME,
  REFERENCE_MEMBER_FIELD_NAME,
} from "config/documentConstant";
import BeforeUnloadModal from "components/Common/BeforeUnloadModal";
import useApproveRequest from "features/document/hooks/create/useApproveRequest";
import useMediaView from "features/common/hooks/useMediaView";
import useHomeButton from "features/document/hooks/create/useHomeButton";
import useFreeTags from "features/document/hooks/create/useFreeTags";
import { useRecoilValue } from "recoil";
import {
  selectedAnalysisBusinessTagState,
  selectedAnalysisCityState,
  selectedAnalysisProvinceState,
} from "store/document/analysisDocument";
import useDocumentTempSave from "features/document/hooks/create/useDocumentTempSave";
import { DocumentForm } from "features/document/utils/getTempSaveRequest";

const ANALYSIS_DOCUMENT_TITLE = "수주정보관리";

interface AnalysisCreateFormStep2Values extends Record<string, any> {
  [ATTACHMENT_FILES]: DocumentAttachmentFileRequest[];
  [DECISION_MANAGER_FIELD_NAME]: string;
  [REFERENCE_MEMBER_FIELD_NAME]: string[];
}

const AnalysisCreateFormStep2Page = () => {
  const navigate = useNavigate();

  const tempDocumentUuid = useParams().uuid;

  /* 데이터: 임시 문서 */
  const { data: tempDocumentDetail } =
    useTempDocumentDetailQuery(tempDocumentUuid);

  const documentClassUuid =
    tempDocumentDetail?.documentClassUuid ||
    DOCUMENT_UUID.ANALYSIS.classes.수주정보.uuid;

  /* 태그 */
  const {
    selectedProvince,
    selectedCity,

    regionTags,
    businessTags,

    getTagUuids,
  } = useAnalysisDocumentTags();

  const { getFreeTagUuids } = useFreeTags({ tempDocumentDetail });

  /* 폼 */
  const methods = useFormContext<DocumentForm>();

  /* BeforeUnload 비활성화 */
  const [beforeUnloadDisable, setBeforeUnloadDisable] = useState(false);

  /* 결재요청 버튼 */
  const approveButtonLabel =
    tempDocumentDetail?.documentApprove?.approveStatus !== "REQ_SUPPLEMENT"
      ? "2단계 결재요청"
      : "수정 후 재요청";

  // 결재요청 토스트
  const {
    open: openStep2CompleteToast,
    onOpenToast: onOpenStep2CompleteToast,
    onOpenChange: onOpenChangeStep2CompleteToast,
  } = useToast();

  const { updateAndApproveRequest } = useApproveRequest();

  /**
   * @description 2단계 결재요청 버튼 클릭 이벤트 핸들러
   */
  const onClickStep2Complete = methods.handleSubmit(
    async (form: AnalysisCreateFormStep2Values) => {
      if (!tempDocumentDetail || !form) return;

      const approverUuid = form[DECISION_MANAGER_FIELD_NAME];
      const referenceUserUuid = form[REFERENCE_MEMBER_FIELD_NAME];

      updateAndApproveRequest({
        updateRequestProps: {
          tempDocumentDetail,
          form,
          tagUuids: getTagUuids(),
          freeTagUuids: getFreeTagUuids(),
          provinceName: selectedProvince,
          cityName: selectedCity || undefined,
          lastLevel: 2,
        },

        approvalRequestProps: {
          approverUuids: approverUuid ? [approverUuid] : undefined,
          referenceUserUuids: referenceUserUuid ? referenceUserUuid : undefined,
          successCallback: () => {
            onOpenStep2CompleteToast(() => {
              setBeforeUnloadDisable(true);
              navigate(
                ROUTER_PATH.myPage.desk +
                  "?selectedTab.name=ANALYSIS&selectedTab.uuid=" +
                  DOCUMENT_CATEGORY_UUID.ANALYSIS +
                  "&page=1",
              );
            });
          },
        },
      });
    },
  );

  /* 임시 저장 */
  const { openTempSaveToast, onOpenChangeTempSaveToast, onTempSaveDoc } =
    useDocumentTempSave({
      tempDocumentDetail,
      documentClassName:
        tempDocumentDetail?.documentClassName ||
        DOCUMENT_UUID.ANALYSIS.classes.수주정보.name,
      documentClassUuid,
      getTagUuids,
      setBeforeUnloadDisable,
    });

  /* Home 버튼 */
  const { openHomeModal, setOpenHomeModal, onClickHomeButton, goToHome } =
    useHomeButton({
      setBeforeUnloadDisable,
    });

  /* 1차 등록 내용 확인 모달 */
  const [openStep1Modal, setOpenStep1Modal] = useState(false);

  /* 모바일 접근 금지 */
  const isSmallView = useMediaView("small");

  if (isSmallView) {
    navigate(-1);
    return;
  }

  return (
    <>
      <WrapStyle>
        <CreateDocumentForm.Header
          title={ANALYSIS_DOCUMENT_TITLE}
          tempDocumentUuid={tempDocumentUuid}
          docTags={{
            지역: regionTags,
            사업구분: businessTags,
          }}
          actions={
            <Button variant="tertiary" onClick={() => setOpenStep1Modal(true)}>
              1차 등록 내용
            </Button>
          }
        />
        <CreateDocumentForm.Body
          tempDocumentUuid={tempDocumentUuid}
          actions={
            <>
              <Button
                size="large"
                variant="tertiary"
                onClick={onClickHomeButton}
              >
                HOME
              </Button>

              <Button size="large" type="button" onClick={onClickStep2Complete}>
                {approveButtonLabel}
              </Button>
            </>
          }
        >
          <Form
            tempDocumentDetail={tempDocumentDetail}
            documentClassUuid={documentClassUuid}
          />
        </CreateDocumentForm.Body>
      </WrapStyle>

      <Modal.Root open={openHomeModal} onOpenChange={setOpenHomeModal}>
        <Modal.Body
          title="작성한 문서를 임시저장 하시겠습니까?"
          onCancel={goToHome}
          cancelValue="나가기"
          onConfirm={() =>
            onTempSaveDoc({
              form: methods.watch(),
              provinceName: selectedProvince,
              cityName: selectedCity || undefined,
              lastLevel: 1,
            })
          }
          confirmValue="임시저장"
        >
          {"'나가기' 클릭 시 작성한 문서는 저장되지 않습니다."}
        </Modal.Body>
      </Modal.Root>

      <Modal.Root open={openStep1Modal} onOpenChange={setOpenStep1Modal}>
        <ContractInfoStep1Modal analysisDocUuid={tempDocumentUuid} />
      </Modal.Root>

      <BeforeUnloadModal
        isDirty={methods.formState.isDirty}
        disable={beforeUnloadDisable}
      />

      <Toast.Message
        open={openTempSaveToast}
        onOpenChange={onOpenChangeTempSaveToast}
      >
        해당 문서가 임시저장 되었습니다.
      </Toast.Message>

      <Toast.Message
        open={openStep2CompleteToast}
        onOpenChange={onOpenChangeStep2CompleteToast}
      >
        ANALYSIS(수주정보관리) 문서의 최종 결재 요청이 완료되었습니다.
      </Toast.Message>
    </>
  );
};

export default AnalysisCreateFormStep2Page;

interface FormProps {
  tempDocumentDetail?: TempDocumentDetailResponse;
  documentClassUuid?: string;
}

const Form = ({ documentClassUuid, tempDocumentDetail }: FormProps) => {
  switch (documentClassUuid) {
    case DOCUMENT_UUID.ANALYSIS.classes.수주정보.uuid:
      return <ContractInfoStep2 tempDocumentDetail={tempDocumentDetail} />;
  }
  return;
};

/* Hooks */

const useAnalysisDocumentTags = () => {
  const province = useRecoilValue(selectedAnalysisProvinceState);
  const city = useRecoilValue(selectedAnalysisCityState);

  const regionTags = useMemo(
    () => (province ? (city ? [`${province} ${city}`] : [province]) : []),
    [province, city],
  );

  const businessTag = useRecoilValue(selectedAnalysisBusinessTagState);
  const [businessTags, setBusinessTags] = useState<
    NoneOrderTagDetailResponse[]
  >([]);

  const getTagUuids = () => businessTags?.map((tag) => tag.tagUuid) || [];

  useEffect(() => {
    if (businessTag) setBusinessTags(() => [businessTag]);
  }, [businessTag]);

  return {
    selectedProvince: province,
    selectedCity: city,

    regionTags,
    businessTags,

    getTagUuids,
  };
};

/* Styles */
export const WrapStyle = styled.div`
  width: 100.6rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;
